/**
 * One aerodrome's weather: the category, the age, the decoded observation, the
 * TAF as a band, and — for a place that publishes nothing — the nearest station
 * that does.
 *
 * The header is the card: `● VFR LJLJ Ljubljana … vor 12 min`. Those four facts
 * answer the question a pilot opens this pane with, and everything under them is
 * the evidence. The raw bulletins are behind a disclosure because they are the
 * text a briefing was signed off on and not the thing anybody reads first.
 *
 * There is no button on this card. The briefing arrives by itself, its raw text
 * goes into the form's Wetter-Beratung lines by itself, and a neighbour's
 * observation is written into a free ATIS row by the same pass — so what is left
 * to show is what HAPPENED, not what could be asked for.
 */
import { useT } from '../../i18n';
import { CategoryDot } from './CategoryDot';
import { NearbyCard } from './NearbyCard';
import { ObsAge } from './ObsAge';
import { TafTimeline } from './TafTimeline';
import { DECODED_ROWS, type StationCard as Model } from './model';

const ROLE_KEY: Record<Model['role'], string> = {
  von: 'wx.role.von',
  nach: 'wx.role.nach',
  alt: 'wx.role.alt',
};

export function StationCard({
  card,
  now,
  raw,
  busy,
  etd,
  eta,
}: {
  card: Model;
  now: number;
  /** the raw-text disclosure is one choice for the whole pane, not one per card */
  raw: boolean;
  /** a briefing is on its way — the only thing that turns "nothing" into "not yet" */
  busy: boolean;
  etd?: number | null | undefined;
  eta?: number | null | undefined;
}) {
  const t = useT();
  const values = card.decoded;

  return (
    <section className="wx-card" aria-label={t(ROLE_KEY[card.role]) + ' ' + card.id}>
      <p className="ae-eyebrow wx-role">{t(ROLE_KEY[card.role])}</p>

      <div className="wx-head">
        <CategoryDot cat={card.category} />
        <span className="wx-id">{card.id}</span>
        {card.name ? <span className="wx-name">{card.name}</span> : null}
        <ObsAge at={card.obsTime} now={now} />
      </div>

      {/* the briefing is on its way: the ident is already useful, a skeleton over
          the whole card is not. Nothing is said when nothing is coming — the
          freshness chip owns the failure, and a card must not claim to be
          waiting for something nobody asked for. */}
      {card.pending && busy ? (
        <p className="wx-loading">
          <span className="wx-loading-rule" aria-hidden="true" />
          <span className="wx-note">{t('wx.card.pending')}</span>
        </p>
      ) : null}

      {card.unavailable ? <p className="wx-note">{t('wx.card.unavailable')}</p> : null}

      {values ? (
        <dl className="wx-values">
          {DECODED_ROWS.filter(k => values[k] !== '').map(k => (
            <div key={k}>
              <dt>{t('wx.row.' + k)}</dt>
              {/* a decoded METAR is derived, and wears the same legend as every
                  other derived value in the app — ice and a dashed rule (§8).
                  The identical leg wind is already ice in the NavLog. */}
              <dd className="ae-num ae-derived" title={t('wx.decoded.title')}>
                {values[k]}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {card.silent && !card.decoded ? <p className="wx-note">{t('wx.nometar.here')}</p> : null}

      {card.nearby ? <NearbyCard card={card.nearby} now={now} etd={etd} eta={eta} /> : null}

      <TafTimeline periods={card.periods} validTo={card.tafValidTo} etd={etd} eta={eta} />

      {(card.metarRaw || card.tafRaw || card.nearby?.metarRaw || card.nearby?.tafRaw) && raw ? (
        <div className="wx-raw">
          {[card.metarRaw, card.tafRaw, card.nearby?.metarRaw ?? '', card.nearby?.tafRaw ?? '']
            .filter(Boolean)
            .map(text => (
              <pre key={text}>{text}</pre>
            ))}
        </div>
      ) : null}

      {/* where this station's observation stands on the printed sheet — and only
          once there IS one: the aerodrome data names a row long before the
          briefing fills it */}
      {card.atisRow && card.decoded ? (
        <div className="wx-foot">
          <span className="ae-chip">{t('wx.card.atisrow', { n: card.atisRow })}</span>
        </div>
      ) : null}
    </section>
  );
}
